import { registerPlugin } from '@capacitor/core';

export interface WatchBiometrics {
  // Vital signs
  heartRate: number;          // bpm (-1 if unavailable)
  hrv: number;                // ms SDNN (-1 if unavailable)
  restingHeartRate: number;   // bpm (-1 if unavailable)
  spo2: number;               // percentage 0-100 (-1 if unavailable)
  respiratoryRate: number;    // breaths/min (-1 if unavailable)
  bodyTemperature: number;    // °C (-1 if unavailable)

  // Fitness
  vo2Max: number;             // mL/kg·min (-1 if unavailable)
  exerciseMinutes: number;    // minutes (-1 if unavailable)
  activeEnergy: number;       // kcal (-1 if unavailable)
  stepCount: number;          // count (-1 if unavailable)

  // Sleep
  sleepHours: number;         // total hours
  sleepStages: {
    awake: number;
    rem: number;
    core: number;
    deep: number;
    inBed: number;
  };

  // Mental health
  mindfulMinutes: number;     // minutes

  // Mobility
  walkingAsymmetry: number;   // percentage (-1 if unavailable)

  // Environment
  noiseExposure: number;      // dB (-1 if unavailable)

  // Cardiac
  heartRateRecovery: number;  // bpm drop in 1min (-1 if unavailable)

  // Meta
  timestamp: string;          // ISO8601
  daysBack: number;
}

export interface ECGResult {
  classification: 'sinusRhythm' | 'atrialFibrillation' | 'highHeartRate' | 'lowHeartRate' | 'poorReading' | 'inconclusive' | 'notSet' | 'unknown';
  averageHeartRate: number;
  date: string;
}

export interface ECGData {
  ecgResults: ECGResult[];
  aFibDetected: boolean;
}

export interface StateOfMindData {
  mood: number;      // -1 if unavailable
  emotion: number;   // -1 if unavailable
  available: boolean;
}

export interface LOGOSHealthKitPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  requestAuthorization(): Promise<{ authorized: boolean }>;
  readAllBiometrics(options?: { daysBack?: number }): Promise<WatchBiometrics>;
  readSleepData(options?: { daysBack?: number }): Promise<{ sleepHours: number; sleepStages: WatchBiometrics['sleepStages'] }>;
  readHeartRateData(options?: { daysBack?: number }): Promise<{ heartRate: number }>;
  readHRVData(options?: { daysBack?: number }): Promise<{ hrv: number }>;
  readExerciseData(options?: { daysBack?: number }): Promise<{ exerciseMinutes: number }>;
  readMindfulMinutes(options?: { daysBack?: number }): Promise<{ mindfulMinutes: number }>;
  readSpO2(options?: { daysBack?: number }): Promise<{ spo2: number }>;
  readVO2Max(options?: { daysBack?: number }): Promise<{ vo2Max: number }>;
  readRestingHeartRate(options?: { daysBack?: number }): Promise<{ restingHeartRate: number }>;
  readRespiratoryRate(options?: { daysBack?: number }): Promise<{ respiratoryRate: number }>;
  readBodyTemperature(options?: { daysBack?: number }): Promise<{ bodyTemperature: number }>;
  readStepCount(options?: { daysBack?: number }): Promise<{ stepCount: number }>;
  readActiveEnergy(options?: { daysBack?: number }): Promise<{ activeEnergy: number }>;
  readECGResults(options?: { daysBack?: number }): Promise<ECGData>;
  readNoiseExposure(options?: { daysBack?: number }): Promise<{ noiseExposure: number }>;
  readWalkingAsymmetry(options?: { daysBack?: number }): Promise<{ walkingAsymmetry: number }>;
  readStateOfMind(): Promise<StateOfMindData>;
}

export const LOGOSHealthKit = registerPlugin<LOGOSHealthKitPlugin>('LOGOSHealthKit');
